import {
  faCalendarCheck,
  faCalendarEdit,
  faCalendarPlus,
  faWifi,
  faWifiSlash,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tab, Tabs } from '@mui/material';
import classNames from 'classnames';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import Button from 'src/components/ui/Button/Button';
import CustomerDetails from 'src/components/ui/CustomerDetails/CustomerDetails';
import DateTooltip from 'src/components/ui/DateTooltip/DateTooltip';
import DetectedErrors from 'src/components/ui/DetectedErrors/DetectedErrors';
import HeatingApplianceDetails from 'src/components/ui/HeatingApplianceDetails/HeatingApplianceDetails';
import HeatingApplianceHistory from 'src/components/ui/HeatingApplianceHistory/HeatingApplianceHistory';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import TicketNotes from 'src/components/ui/TicketNotes/TicketNotes';
import { space } from 'src/constants/transConstants';
import { addModal, removeModal } from 'src/features/globalSlice';
import { routes } from 'src/routes';
import { getDeviceDetails, setDeviceDetails } from 'src/store/actions/deviceActions';
import { getTicketDetails, setTicketDetails } from 'src/store/actions/ticketActions';
import { ModalType } from 'src/store/models/Modal';
import { TicketStatus } from 'src/store/models/Ticket';
import { colors } from 'src/utils/colors';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';

import classes from './ServiceTicketDetails.module.scss';

const listOfUsersWithInsight = [
  'mihael.medo123+test@gmail.com',
  'martin.zerjal@atech.si',
  'davor.jakulin@fumis.si',
  'davor@jakulin.com',
  'martin.zerjal@gmail.com',
  'alex@pf2024.com',
  'tom@gmail.com',
];

const ServiceTicketDetails: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const [tabValue, setTabValue] = useState<'parameters' | 'insight'>('parameters');

  const loading = useLoadingSelector({
    actionType: getTicketDetails.toString(),
  });
  const deviceLoading = useLoadingSelector({
    actionType: getDeviceDetails.toString(),
  });
  const ticket = useAppSelector((state) => state.ticket.ticketDetails);
  const device = useAppSelector((state) => state.device.deviceDetails);
  const user = useAppSelector((state) => state.auth.user);
  const isInProgress = ticket?.status === TicketStatus.IN_PROGRESS;
  const isResolved = ticket?.status === TicketStatus.RESOLVED;

  useEffect(() => {
    dispatch(setTicketDetails(null));
    dispatch(getTicketDetails({ id: params?.ticketId }));
    return () => {
      dispatch(setDeviceDetails(null));
    };
    // eslint-disable-next-line
  }, []);

  const onHeaderButtonClick = () => {
    if (isInProgress) {
      navigate(`${location.pathname}${routes.RESOLVE}`);
    } else {
      dispatch(
        addModal({
          modalType: ModalType.PIN,
          title: t('ticket_details:enter_pin'),
          body: `${t('common:contact')} ${ticket?.user?.title}: ${ticket?.user?.phone}`,
          onBackdropClose: false,
          secondaryAction: () => dispatch(removeModal()),
          secondaryButtonText: t('common:cancel'),
        })
      );
    }
  };

  const buttonColor = isInProgress ? colors()?.colorFumisGreen1 : colors()?.colorRed;

  const headerRight = (
    <div className={classes.HeaderRight}>
      <DateTooltip
        tooltipTitle={t('tickets:assign_date')}
        icon={faCalendarPlus}
        date={ticket?.assignedAt}
      />
      <DateTooltip
        tooltipTitle={t('tickets:start_date')}
        icon={faCalendarEdit}
        date={ticket?.startedAt}
      />
      {isResolved && (
        <DateTooltip
          tooltipTitle={t('tickets:resolved_date')}
          icon={faCalendarCheck}
          date={ticket?.resolvedAt}
        />
      )}
      {ticket && !isResolved ? (
        <Button
          sx={{
            backgroundColor: buttonColor,
            ':hover': {
              backgroundColor: buttonColor,
            },
          }}
          className={classes.HeaderButton}
          disabled={!ticket}
          onClick={onHeaderButtonClick}
        >
          {isInProgress ? t('tickets:resolve_ticket') : t('tickets:start_ticket')}
        </Button>
      ) : null}
    </div>
  );

  return (
    <div className={classes.Container}>
      <PageHeader
        title={ticket ? `${t('common:ticket')} ${ticket?.ticketId}` : space}
        showBackButton
        endNode={headerRight}
      />
      <h4>{t('ticket_details:ticket_details')}</h4>
      <section>
        <CustomerDetails ticket={ticket} loading={loading} />
        <aside>
          <DetectedErrors loading={loading || deviceLoading} />
          <TicketNotes disabled={isResolved || loading} />
        </aside>
      </section>
      {ticket && !isResolved ? (
        <section>
          <div className={classes.SecondHeader}>
            {listOfUsersWithInsight.includes(user?.eMail) ? (
              <Tabs
                className={classes.Tabs}
                value={tabValue}
                onChange={(_, value) => setTabValue(value as 'parameters' | 'insight')}
              >
                <Tab label={t('ticket_details:real_time_data')} value="parameters" />
                <Tab label={t('ticket_details:insight')} value="insight" />
              </Tabs>
            ) : (
              <h4>{t('tickets:appliance_details')}</h4>
            )}
            <p>
              {`${t('common:status')}: `}
              <span
                className={classNames({
                  [classes.Connected]: isInProgress && device,
                })}
              >
                {(isInProgress && device
                  ? t('ticket_details:connected')
                  : t('ticket_details:disconnected')
                ).toLowerCase()}
              </span>
              <FontAwesomeIcon
                className={classNames({
                  [classes.Connected]: isInProgress && device,
                })}
                icon={isInProgress && device ? faWifi : faWifiSlash}
              />
            </p>
            <Button
              onClick={() =>
                navigate(`${routes.TICKETS}/${params?.ticketId}${routes.EDIT_PARAMETERS}`)
              }
              disabled={!isInProgress}
              variant="outlined"
            >
              {t('ticket_details:edit_parameters')}
            </Button>
          </div>
          {tabValue === 'parameters' ? <HeatingApplianceDetails /> : <HeatingApplianceHistory />}
        </section>
      ) : (
        <HeatingApplianceDetails />
      )}
    </div>
  );
};

export default ServiceTicketDetails;
